import { Component, HostListener, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { CustomerService } from '../../services/customer-service/customer.service';
import { GroupService } from '../../services/group-service/group.service';
import {
  CanComponentDeactivate,
  confirmDiscardUnsavedChanges,
} from '../../guards/unsaved-changes.guard';
import { UnsavedChangesTrackerService } from '../../services/unsaved-changes/unsaved-changes-tracker.service';
import { UnsavedChangesHostHelper } from '../../services/unsaved-changes/unsaved-changes-host.helper';

const CUSTOMER_GROUP_MGMT_DIRTY_SOURCE = 'customer-group-management';

export interface GroupItem {
  id: number | string;
  groupName: string;
  isCommon: boolean;
  wiliotGatewayFormatEnabled: boolean;
}

export interface CustomerItem {
  id: number | string;
  customerName: string;
}

@Component({
  selector: 'app-group-assignment-management',
  standalone: true,
  imports: [CommonModule, RouterLink, FormsModule, MatIconModule],
  templateUrl: './group-assignment-management.component.html',
  styleUrl: './group-assignment-management.component.scss',
})
export class GroupAssignmentManagementComponent
  implements OnInit, OnDestroy, CanComponentDeactivate
{
  // ── State ──────────────────────────────────────────────────────────────────
  selectedCustomerId: number | string = '';
  existingSearch = '';
  availableSearch = '';

  customers: CustomerItem[] = [];
  existingGroups: GroupItem[] = [];
  availableGroups: GroupItem[] = [];

  selectedExisting: (number | string)[] = [];
  selectedAvailable: (number | string)[] = [];

  isLoadingCustomers = false;
  isLoadingGroups = false;
  isSaving = false;

  /** Baseline existing-group IDs after last load/save (for dirty detection). */
  private initialExistingIds: string[] = [];
  private previousCustomerId: number | string = '';

  private readonly dialog = inject(MatDialog);
  private readonly unsavedChanges = inject(UnsavedChangesTrackerService);
  private readonly unsavedHost = new UnsavedChangesHostHelper(
    this.unsavedChanges,
    this.dialog,
    inject(Router)
  );

  // ── Payload for customer search ────────────────────────────────────────────
  private readonly customerSearchPayload = {
    page: 0,
    size: 500,
    filters: [{ field: '', operator: '', value: '' }],
    sorts: [{ field: 'customerName', direction: 'asc' }],
  };

  constructor(
    private customerService: CustomerService,
    private groupService: GroupService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.loadCustomers();
  }

  ngOnDestroy(): void {
    this.unsavedHost.clearOnDestroy();
  }

  canDeactivate(): Promise<boolean> {
    return this.unsavedHost.canDeactivate();
  }

  @HostListener('window:beforeunload', ['$event'])
  onWindowBeforeUnload(event: BeforeUnloadEvent): void {
    this.unsavedHost.onBeforeUnload(event);
  }

  private clearSelections(): void {
    this.selectedExisting = [];
    this.selectedAvailable = [];
  }

  private captureAssignmentSnapshot(): void {
    this.initialExistingIds = this.existingGroups
      .map((g) => String(g.id))
      .sort();
  }

  private hasAssignmentChanges(): boolean {
    const current = this.existingGroups.map((g) => String(g.id)).sort();
    if (current.length !== this.initialExistingIds.length) {
      return true;
    }
    return current.some((id, index) => id !== this.initialExistingIds[index]);
  }

  private syncUnsavedChangesState(): void {
    this.unsavedChanges.setDirty(
      CUSTOMER_GROUP_MGMT_DIRTY_SOURCE,
      this.hasAssignmentChanges()
    );
  }

  // ── Filtered views (search) ────────────────────────────────────────────────
  get filteredExisting(): GroupItem[] {
    const term = this.existingSearch.toLowerCase().trim();
    return term
      ? this.existingGroups.filter((g) =>
          g.groupName.toLowerCase().includes(term)
        )
      : this.existingGroups;
  }

  get filteredAvailable(): GroupItem[] {
    const term = this.availableSearch.toLowerCase().trim();
    return term
      ? this.availableGroups.filter((g) =>
          g.groupName.toLowerCase().includes(term)
        )
      : this.availableGroups;
  }

  // ── Data loading ───────────────────────────────────────────────────────────
  private loadCustomers(): void {
    this.isLoadingCustomers = true;
    this.customerService.getAllCustomer(this.customerSearchPayload).subscribe({
      next: (res: any) => {
        this.customers = res?.data?.content ?? res?.content ?? [];
        this.isLoadingCustomers = false;
      },
      error: (err: any) => {
        console.error('Failed to load customers:', err);
        this.toastr.error('Failed to load customer list.', 'Error', {
          timeOut: 5000,
          closeButton: true,
        });
        this.isLoadingCustomers = false;
      },
    });
  }

  async onCustomerChange(): Promise<void> {
    if (this.unsavedChanges.hasUnsavedChanges()) {
      const discard = await confirmDiscardUnsavedChanges(this.dialog);
      if (!discard) {
        this.selectedCustomerId = this.previousCustomerId;
        return;
      }
      this.unsavedChanges.requestDiscard();
    }

    this.previousCustomerId = this.selectedCustomerId;

    if (!this.selectedCustomerId) {
      this.existingGroups = [];
      this.availableGroups = [];
      this.clearSelections();
      this.captureAssignmentSnapshot();
      this.syncUnsavedChangesState();
      return;
    }
    this.loadGroupsForCustomer(this.selectedCustomerId);
  }

  private loadGroupsForCustomer(customerId: number | string): void {
    this.isLoadingGroups = true;
    this.clearSelections();
    this.existingGroups = [];
    this.availableGroups = [];

    this.groupService.getGroupsForAssignment(customerId).subscribe({
      next: (res: any) => {
        const payload = res?.data ?? res;

        const toItem = (g: any): GroupItem => ({
          id: g.id,
          groupName: g.groupName ?? '',
          isCommon: g.isCommon ?? false,
          wiliotGatewayFormatEnabled: g.wiliotGatewayFormatEnabled ?? false,
        });

        // Shape A: { assignedGroups: [...], availableGroups: [...] }
        if (payload?.assignedGroups || payload?.availableGroups) {
          this.existingGroups  = (payload.assignedGroups  ?? []).map(toItem);
          this.availableGroups = (payload.availableGroups ?? []).map(toItem);
          this.isLoadingGroups = false;
          this.captureAssignmentSnapshot();
          this.syncUnsavedChangesState();
          return;
        }

        // Shape B: flat array — assigned groups have an explicit `assigned: true` flag
        if (Array.isArray(payload)) {
          const hasAssignedFlag = payload.some((g: any) => g.assigned === true || g.assigned === false);
          if (hasAssignedFlag) {
            this.existingGroups  = payload.filter((g: any) => g.assigned === true).map(toItem);
            this.availableGroups = payload.filter((g: any) => g.assigned !== true).map(toItem);
            this.isLoadingGroups = false;
            this.captureAssignmentSnapshot();
            this.syncUnsavedChangesState();
            return;
          }

          // Shape C: flat array — assigned groups have a non-null customerGroupId
          this.existingGroups  = payload.filter((g: any) => g.customerGroupId != null).map(toItem);
          this.availableGroups = payload.filter((g: any) => g.customerGroupId == null).map(toItem);
          this.isLoadingGroups = false;
          this.captureAssignmentSnapshot();
          this.syncUnsavedChangesState();
          return;
        }

        // Fallback — unexpected shape, treat everything as available
        console.warn('[GroupAssignment] Unexpected API response shape:', payload);
        this.availableGroups = [];
        this.existingGroups  = [];
        this.isLoadingGroups = false;
        this.captureAssignmentSnapshot();
        this.syncUnsavedChangesState();
      },
      error: (err: any) => {
        console.error('Failed to load groups:', err);
        this.toastr.error('Failed to load groups for selected customer.', 'Error', {
          timeOut: 5000,
          closeButton: true,
        });
        this.isLoadingGroups = false;
        this.captureAssignmentSnapshot();
        this.syncUnsavedChangesState();
      },
    });
  }

  // ── Selection toggles ──────────────────────────────────────────────────────
  toggleExisting(group: GroupItem): void {
    const idx = this.selectedExisting.indexOf(group.id);
    if (idx >= 0) {
      this.selectedExisting.splice(idx, 1);
    } else {
      this.selectedExisting.push(group.id);
    }
  }

  toggleAvailable(id: number | string): void {
    const idx = this.selectedAvailable.indexOf(id);
    if (idx >= 0) {
      this.selectedAvailable.splice(idx, 1);
    } else {
      this.selectedAvailable.push(id);
    }
  }

  isExistingSelected(id: number | string): boolean {
    return this.selectedExisting.includes(id);
  }

  isAvailableSelected(id: number | string): boolean {
    return this.selectedAvailable.includes(id);
  }

  // ── Transfer actions ───────────────────────────────────────────────────────

  /** Move selected Existing → Available */
  moveToAvailable(): void {
    const toMove = this.existingGroups.filter((g) =>
      this.selectedExisting.includes(g.id)
    );
    this.existingGroups = this.existingGroups.filter(
      (g) => !this.selectedExisting.includes(g.id)
    );
    this.availableGroups = [...this.availableGroups, ...toMove].sort((a, b) =>
      a.groupName.localeCompare(b.groupName)
    );
    this.selectedExisting = [];
    this.syncUnsavedChangesState();
  }

  /** Move ALL Existing → Available */
  moveAllToAvailable(): void {
    this.availableGroups = [...this.availableGroups, ...this.existingGroups].sort(
      (a, b) => a.groupName.localeCompare(b.groupName)
    );
    this.existingGroups = [];
    this.selectedExisting = [];
    this.syncUnsavedChangesState();
  }

  /** Move selected Available → Existing */
  moveToExisting(): void {
    const toMove = this.availableGroups.filter((g) =>
      this.selectedAvailable.includes(g.id)
    );
    this.availableGroups = this.availableGroups.filter(
      (g) => !this.selectedAvailable.includes(g.id)
    );
    this.existingGroups = [...this.existingGroups, ...toMove].sort((a, b) =>
      a.groupName.localeCompare(b.groupName)
    );
    this.selectedAvailable = [];
    this.syncUnsavedChangesState();
  }

  /** Move ALL Available → Existing */
  moveAllToExisting(): void {
    this.existingGroups = [...this.existingGroups, ...this.availableGroups].sort(
      (a, b) => a.groupName.localeCompare(b.groupName)
    );
    this.availableGroups = [];
    this.selectedAvailable = [];
    this.syncUnsavedChangesState();
  }

  // ── Save / Cancel ──────────────────────────────────────────────────────────
  onSave(): void {
    if (!this.selectedCustomerId) {
      this.toastr.warning('Please select a customer before saving.', 'No Customer Selected', {
        timeOut: 4000,
        closeButton: true,
      });
      return;
    }

    this.isSaving = true;
    const assignments = this.existingGroups.map((g) => ({
      groupId: g.id,
      wiliotGatewayFormatEnabled: g.wiliotGatewayFormatEnabled ?? false,
    }));

    this.groupService
      .assignGroupsToCustomer(this.selectedCustomerId, assignments)
      .subscribe({
        next: (response: any) => {
          this.toastr.success(
            response?.message ?? 'Group assignments saved successfully.',
            'Assignments Saved',
            { timeOut: 30000, closeButton: true, progressBar: true }
          );
          this.isSaving = false;
          this.captureAssignmentSnapshot();
          this.syncUnsavedChangesState();
        },
        error: (error: any) => {
          const msg =
            error?.error?.message ??
            error?.message ??
            'Failed to save group assignments. Please try again.';
          this.toastr.error(msg, 'Save Failed', {
            timeOut: 8000,
            closeButton: true,
            progressBar: true,
          });
          this.isSaving = false;
        },
      });
  }

  onCancel(): void {
    if (this.selectedCustomerId) {
      this.loadGroupsForCustomer(this.selectedCustomerId);
    }
  }
}
